import { ukDate } from "../lib/dates";
import React, { useEffect, useRef, useState } from "react";
import { PortalMessages } from "../components/PortalMessages";
import { ProfileCheckCard } from "../components/ProfileCheckCard";
import { ProfileExtraFields, profileExtrasFrom } from "../components/ProfileExtraFields";
import { ProfilePhotoEditor } from "../components/ProfilePhoto";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { usePaged, Paginator } from "../components/Paginator";
import { BannerMessagePreview } from "../components/BannerMessage";
import { MyCellGroupPanel } from "../components/MyCellGroupPanel";
import { FirstTimerWelcomeCard } from "../components/FirstTimerWelcomeCard";
import { LatestSermonCard } from "../components/LatestSermonCard";
import { MySermonNotes } from "../components/MySermonNotes";
import { CalendarCheck, User, Mail, Phone, MapPin, Flame, Heart, MessagesSquare, HandHeart, Check, Clock, Camera, Pencil, FileText, Download, ExternalLink, Lock, Award, LayoutDashboard, GraduationCap, BookOpen, ChevronRight, Play, Mic, Video, Youtube, Facebook, Link2, Sparkles, Layers, CalendarClock, AlertTriangle, Upload } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const sermonKind = (ct = "") => ct.startsWith("audio") ? "audio" : ct.startsWith("video") ? "video" : "doc";
const youtubeId = (url = "") => { const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/); return m ? m[1] : null; };
const sermonLinkInfo = (url = "") => {
  const yt = youtubeId(url);
  if (yt) return { platform: "youtube", label: "YouTube", embed: `https://www.youtube.com/embed/${yt}` };
  if (/facebook\.com|fb\.watch/i.test(url)) return { platform: "facebook", label: "Facebook", embed: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false` };
  return { platform: "other", label: "Watch", embed: null };
};

export default function Portal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [sermonsList, setSermonsList] = useState([]);
  const [playingSermon, setPlayingSermon] = useState(null);
  const [answerFor, setAnswerFor] = useState(null);
  const [testimonyText, setTestimonyText] = useState("");
  const [shareTestimony, setShareTestimony] = useState(false);
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [testimonies, setTestimonies] = useState([]);
  const [portalSeries, setPortalSeries] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [prayers, setPrayers] = useState([]);
  const [myDocs, setMyDocs] = useState({ files: [], collab: [] });
  const [certs, setCerts] = useState([]);
  const [downloadingCert, setDownloadingCert] = useState("");
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [checkingIn, setCheckingIn] = useState("");
  const [reminderOptIn, setReminderOptIn] = useState(false);
  const [reminderSchedules, setReminderSchedules] = useState([]);
  const [reminderIds, setReminderIds] = useState([]);
  const [savingReminder, setSavingReminder] = useState(false);
  const [replyEmailOptIn, setReplyEmailOptIn] = useState(true);
  const [savingReplyEmail, setSavingReplyEmail] = useState(false);
  const [attStats, setAttStats] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightDoc = searchParams.get("doc");
  const [prayerText, setPrayerText] = useState("");
  const [prayerPrivate, setPrayerPrivate] = useState(false);
  const [submittingPrayer, setSubmittingPrayer] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pform, setPform] = useState({ first_name: "", last_name: "", email: "", phone: "", join_date: "", address: "", postcode: "", date_of_birth: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [tab, setTab] = useState(searchParams.get("doc") ? "documents" : "dashboard");
  const endRef = useRef();
  const avatarRef = useRef();

  const loadConversation = async () => {
    const r = await api.get("/portal/conversation");
    setMessages(r.data.messages || []);
  };
  const loadPrayers = async () => {
    const r = await api.get("/portal/prayer-requests");
    setPrayers(r.data || []);
  };
  const loadCourses = async () => {
    try { const r = await api.get("/portal/courses"); setEnrolledCourses(r.data.enrolled || []); } catch {}
  };
  const loadSermons = async () => {
    try { const r = await api.get("/media", { params: { category: "sermon" } }); setSermonsList(r.data || []); } catch {}
  };
  const loadTestimonies = async () => {
    try { const r = await api.get("/portal/testimonies"); setTestimonies(r.data || []); } catch {}
  };
  const loadUpcomingServices = async () => {
    try { const r = await api.get("/portal/services"); setUpcomingServices(r.data || []); } catch {}
    try {
      const rs = await api.get("/portal/reminder-settings");
      setReminderOptIn(!!rs.data.enabled);
      setReminderSchedules(rs.data.schedules || []);
      setReminderIds(rs.data.schedule_ids || []);
    } catch {}
    try { const st = await api.get("/portal/attendance-stats"); setAttStats(st.data); } catch {}
  };
  const saveReminders = async (enabled, ids) => {
    setSavingReminder(true);
    try {
      await api.put("/portal/reminder-settings", { enabled, schedule_ids: ids });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingReminder(false); }
  };
  const toggleReminder = async () => {
    const next = !reminderOptIn;
    setReminderOptIn(next);
    await saveReminders(next, reminderIds);
    toast.success(next ? "We'll send you a weekly reminder of upcoming services" : "Reminders turned off");
  };
  const toggleReminderService = async (id) => {
    const next = reminderIds.includes(id) ? reminderIds.filter((x) => x !== id) : [...reminderIds, id];
    setReminderIds(next);
    await saveReminders(reminderOptIn, next);
  };
  const loadReplyEmail = async () => {
    try { const r = await api.get("/portal/reply-email-setting"); setReplyEmailOptIn(!!r.data.enabled); } catch {}
  };
  const toggleReplyEmail = async () => {
    const next = !replyEmailOptIn;
    setReplyEmailOptIn(next);
    setSavingReplyEmail(true);
    try {
      await api.put("/portal/reply-email-setting", { enabled: next });
      toast.success(next ? "We'll email you when a tutor replies" : "Tutor-reply emails turned off");
    } catch (err) { setReplyEmailOptIn(!next); toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingReplyEmail(false); }
  };
  const checkInService = async (svc, attending) => {
    setCheckingIn(svc.id);
    try {
      await api.post(`/portal/services/${svc.id}/checkin`, { attending });
      setUpcomingServices((list) => list.map((s) => s.id === svc.id ? { ...s, checked_in: attending } : s));
      toast.success(attending ? "You're registered — see you there!" : "Attendance removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCheckingIn(""); }
  };

  useEffect(() => {
    (async () => {
      const r = await api.get("/portal/me"); setData(r.data);
      await loadConversation();
      await loadPrayers();
      await loadCourses();
      await loadSermons();
      await loadTestimonies();
      await loadUpcomingServices();
      await loadReplyEmail();
      try { const r2 = await api.get("/portal/documents"); setMyDocs(r2.data || { files: [], collab: [] }); } catch {}
      try { const r3 = await api.get("/portal/certificates"); setCerts(r3.data || []); } catch {}
    })();
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/portal/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get("/portal/me"); setData(r.data);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAvatarUploading(false); if (avatarRef.current) avatarRef.current.value = ""; }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (!prayerText.trim()) return;
    setSubmittingPrayer(true);
    try {
      await api.post("/portal/prayer-requests", { request: prayerText, is_private: prayerPrivate });
      toast.success("Your prayer request has been sent 🙏");
      setPrayerText(""); setPrayerPrivate(false); loadPrayers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmittingPrayer(false); }
  };

  const openAnswer = (p) => { setAnswerFor(p); setTestimonyText(p.testimony || ""); setShareTestimony(!!p.testimony_shared); };
  const saveAnswer = async (e) => {
    e.preventDefault();
    setSavingAnswer(true);
    try {
      await api.patch(`/portal/prayer-requests/${answerFor.id}`, { status: "answered", testimony: testimonyText, share_testimony: shareTestimony });
      toast.success(shareTestimony ? "Testimony shared 🙌" : "Marked as answered 🙌");
      setAnswerFor(null); setTestimonyText(""); setShareTestimony(false); loadPrayers(); loadTestimonies();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAnswer(false); }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed — you may no longer have access."); }
  };

  const downloadCert = async (c) => {
    setDownloadingCert(c.course_id);
    try {
      const res = await api.get(`/portal/courses/${c.course_id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${(c.title || "Course").replace(/[^\w -]/g, "").replace(/\s+/g, "_")}_Certificate.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded 🎓");
    } catch { toast.error("Could not download certificate"); }
    finally { setDownloadingCert(""); }
  };

  const [banner, setBanner] = useState(null);
  useEffect(() => { api.get("/portal-banner").then(({ data }) => setBanner(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!highlightDoc) return;
    if (!(myDocs.files.length || myDocs.collab.length)) return;
    const el = document.querySelector(`[data-testid="portal-doc-${highlightDoc}"]`) || document.querySelector('[data-testid="portal-documents"]');
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  }, [highlightDoc, myDocs]);

  const openEditProfile = () => {
    const m = data?.member || {};
    setPform({
      first_name: m.first_name || data?.user?.name?.split(" ")[0] || "", last_name: m.last_name || (data?.user?.name?.split(" ").slice(1).join(" ")) || "",
      email: m.email || data?.user?.email || "", phone: m.phone || "", join_date: m.join_date || "",
      address: m.address || "", postcode: m.postcode || "", date_of_birth: m.date_of_birth || "",
      ...profileExtrasFrom(m),
    });
    setEditOpen(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/portal/me", pform);
      const r = await api.get("/portal/me"); setData(r.data);
      toast.success("Profile updated");
      setEditOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const coursesPaged = usePaged(enrolledCourses, 9);
  const sermonSeries = [...new Set(sermonsList.map((s) => (s.series || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filteredSermons = (portalSeries ? sermonsList.filter((s) => (s.series || "").trim() === portalSeries) : sermonsList)
    .slice()
    .sort((a, b) => {
      const da = a.sermon_date || a.created_at || "", dbb = b.sermon_date || b.created_at || "";
      return portalSeries ? da.localeCompare(dbb) : dbb.localeCompare(da);
    });
  const sermonsPaged = usePaged(filteredSermons, 9);
  const latestSermon = [...sermonsList].sort((a, b) => (b.sermon_date || b.created_at || "").localeCompare(a.sermon_date || a.created_at || ""))[0] || null;

  if (!data) return <div className="p-8" style={{ color: "var(--sj-text-3)" }}>Loading...</div>;
  const { user, services_attended, total_services } = data;
  const member = data.member || {};
  const noProfile = !data.member;
  const rate = total_services ? Math.round((services_attended / total_services) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {noProfile && (
        <div data-testid="portal-setup-prompt" className={`${CARD} p-6 flex flex-col sm:flex-row sm:items-center gap-4`} style={{ background: "#FFF8EC", borderColor: "#F3D9A6" }}>
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#FCEBC7" }}>
            <User className="h-6 w-6" style={{ color: "#B45309" }} />
          </div>
          <div className="flex-1">
            <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Set up your member profile</h2>
            <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>You don't have a personal profile yet. Add your details to join the church family directory and use the full member portal.</p>
          </div>
          <button data-testid="portal-setup-btn" onClick={openEditProfile} className="rounded-full px-5 py-2.5 text-white font-semibold text-sm shrink-0 hover:brightness-105" style={{ background: "var(--sj-primary)" }}>Set up my profile</button>
        </div>
      )}

      {(() => {
        const flat = (enrolledCourses || []).flatMap((c) => (c.resubmit_needed || []).map((r) => ({ ...r, course_id: c.id, course_title: c.title })));
        if (flat.length === 0) return null;
        return (
          <div data-testid="portal-resubmit-alert" className="rounded-2xl border p-4 sm:p-5" style={{ borderColor: "#E9CBA3", background: "#FFF7EC" }}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F6D9A8" }}><AlertTriangle className="h-5 w-5" style={{ color: "#8a5a1a" }} /></div>
              <div className="min-w-0 flex-1">
                <h2 className="font-head text-lg font-bold" style={{ color: "#8a5a1a" }}>Action needed: resubmit {flat.length === 1 ? "an assignment" : `${flat.length} assignments`}</h2>
                <p className="text-sm mt-0.5" style={{ color: "#8a5a1a" }}>Your tutor has asked you to submit again. Open the assignment to replace your work.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {flat.map((r, i) => (
                    <button key={i} data-testid={`portal-resubmit-jump-${r.course_id}-${r.lesson_id}`} onClick={() => navigate(`/my-classes/${r.course_id}`)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white hover:brightness-105" style={{ background: "#8a5a1a" }}>
                      <Upload className="h-3.5 w-3.5" /> {r.course_title} · {r.lesson_title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="portal-tabs">
        {[
          { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
          { id: "messages", label: "Messages", icon: MessagesSquare },
          { id: "courses", label: "My Courses", icon: GraduationCap },
          { id: "sermons", label: "Latest Sermons", icon: Mic },
          { id: "documents", label: "My Documents", icon: FileText },
          { id: "prayer", label: "Prayer Requests", icon: HandHeart },
          { id: "cell", label: "Cell Group", icon: MapPin },
          { id: "profile", label: "My Profile", icon: User },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap border transition-colors shrink-0"
              style={active ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && (<>
      <ProfileCheckCard member={member} onEdit={openEditProfile} onDone={() => api.get("/portal/me").then((r) => setData(r.data))} />
      <LatestSermonCard />
      <MySermonNotes />
      <FirstTimerWelcomeCard />
      <div className="sj-hero" data-testid="portal-banner">
        <div className="p-8 md:p-10 text-white">
          <div className="sj-eyebrow flex items-center gap-2"><Flame className="h-4 w-4" /> Streams of Joy Erith London · Member Portal</div>
          <div className="mt-4 flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="h-20 w-20 rounded-full overflow-hidden ring-4 ring-white/40 bg-white/20 flex items-center justify-center">
                {member?.avatar_file_id ? (
                  <img src={mediaUrl(member.avatar_file_id)} alt="" data-testid="portal-avatar-img" className="h-full w-full object-cover" />
                ) : (
                  <User className="h-9 w-9 text-white/80" />
                )}
              </div>
              <input ref={avatarRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAvatar} data-testid="portal-avatar-input" />
              <button data-testid="portal-avatar-button" disabled={avatarUploading} onClick={() => avatarRef.current?.click()}
                className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white flex items-center justify-center shadow-md hover:brightness-95 disabled:opacity-60" style={{ color: "var(--sj-primary)" }} title="Change photo">
                <Camera className="h-4 w-4" />
              </button>
            </div>
            <div>
              <h1 className="font-head text-3xl md:text-4xl font-extrabold tracking-tight">Welcome, <span style={{ color: "var(--sj-accent)" }}>{user.name?.split(" ")[0]}</span>!</h1>
              <p className="mt-1 text-white/85 max-w-lg">We're so glad you're part of the family. Here is your member portal.</p>
            </div>
          </div>
          {banner?.enabled && banner?.message && <BannerMessagePreview message={banner.message} reference={banner.reference} />}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className={`${CARD} p-6`} data-testid="portal-attended">
          <CalendarCheck className="h-7 w-7" style={{ color: "#0B5E4E" }} />
          <div className="mt-3 font-head text-3xl font-extrabold">{services_attended}</div>
          <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Services Attended</div>
        </div>
        <div className={`${CARD} p-6`}>
          <Heart className="h-7 w-7" style={{ color: "#17A88A" }} />
          <div className="mt-3 font-head text-3xl font-extrabold">{rate}%</div>
          <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Attendance Rate</div>
        </div>
        <div className={`${CARD} p-6`}>
          <MessagesSquare className="h-7 w-7" style={{ color: "#084438" }} />
          <div className="mt-3 font-head text-3xl font-extrabold" data-testid="portal-stat-inbox">{messages.length}</div>
          <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Messages in inbox</div>
        </div>
      </div>

      {upcomingServices.length > 0 && (
        <div className={`${CARD} p-6 md:p-7`} data-testid="portal-services-checkin">
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-lg font-semibold">Register your attendance</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Streams of Joy welcomes you! Tap “Yes, I'm attending” for the service(s) you'll join — it counts towards your attendance.</p>
          {attStats && (attStats.current_streak > 0 || attStats.total_attended > 0) && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl p-3.5" style={{ background: "linear-gradient(100deg,#FFF3E6,#FDE8D6)" }} data-testid="portal-streak">
              <div className="flex items-center gap-2">
                <Flame className="h-6 w-6" style={{ color: "#B45309" }} />
                <div>
                  <div className="font-head text-xl font-extrabold" style={{ color: "#8a5a1a" }} data-testid="portal-streak-count">{attStats.current_streak}-service streak</div>
                  <div className="text-xs" style={{ color: "#8a5a1a" }}>{attStats.current_streak > 0 ? "Keep it going — see you at the next service!" : "Register today to start a new streak!"}</div>
                </div>
              </div>
              <div className="ml-auto flex gap-4 text-center">
                <div><div className="font-head text-lg font-bold" style={{ color: "#8a5a1a" }}>{attStats.total_attended}</div><div className="text-[10px] uppercase tracking-wide" style={{ color: "#a8763a" }}>Attended</div></div>
                <div><div className="font-head text-lg font-bold" style={{ color: "#8a5a1a" }}>{attStats.longest_streak}</div><div className="text-[10px] uppercase tracking-wide" style={{ color: "#a8763a" }}>Best streak</div></div>
              </div>
            </div>
          )}
          <div className="space-y-2.5">
            {upcomingServices.map((s) => (
              <div key={s.id} data-testid={`portal-service-${s.id}`} className="flex items-center justify-between gap-3 rounded-xl border p-3.5" style={{ borderColor: s.checked_in ? "var(--sj-primary)" : "var(--sj-border)", background: s.checked_in ? "#F2F6F5" : "#fff" }}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{s.title}</span>
                    {s.is_today && <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>Today</span>}
                  </div>
                  <div className="text-xs flex items-center gap-1.5 mt-0.5" style={{ color: "var(--sj-text-3)" }}>
                    <Clock className="h-3.5 w-3.5" /> {new Date(s.service_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{s.time_label ? ` · ${s.time_label}` : ""}
                  </div>
                </div>
                {s.checked_in ? (
                  <button data-testid={`portal-checkout-${s.id}`} disabled={checkingIn === s.id} onClick={() => checkInService(s, false)}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white shrink-0 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                    <Check className="h-4 w-4" /> Attending
                  </button>
                ) : (
                  <button data-testid={`portal-checkin-${s.id}`} disabled={checkingIn === s.id} onClick={() => checkInService(s, true)}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border shrink-0 hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}>
                    Yes, I'm attending
                  </button>
                )}
              </div>
            ))}
          </div>
          <label data-testid="portal-reminder-toggle" className="mt-4 flex items-center justify-between gap-3 rounded-xl border p-3.5 cursor-pointer" style={{ borderColor: "var(--sj-border)", background: "#F2F6F5" }}>
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} />
              <span className="text-sm font-medium">Send me a weekly reminder of upcoming services</span>
            </div>
            <input type="checkbox" checked={reminderOptIn} disabled={savingReminder} onChange={toggleReminder} className="h-5 w-5 accent-[#0B5E4E] shrink-0" />
          </label>
          {reminderOptIn && reminderSchedules.length > 0 && (
            <div className="mt-2 rounded-xl border p-3.5" style={{ borderColor: "var(--sj-border)" }} data-testid="portal-reminder-picker">
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--sj-text-3)" }}>Which services? {reminderIds.length === 0 && <span className="normal-case font-normal">(all, by default)</span>}</div>
              <div className="flex flex-wrap gap-2">
                {reminderSchedules.map((s) => {
                  const on = reminderIds.includes(s.id);
                  return (
                    <button key={s.id} data-testid={`reminder-service-${s.id}`} disabled={savingReminder} onClick={() => toggleReminderService(s.id)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-60"
                      style={on ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                      {on && <Check className="h-3 w-3" />}{s.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] mt-2" style={{ color: "var(--sj-text-3)" }}>Pick the ones you usually attend, or leave none selected to be reminded about all services.</p>
            </div>
          )}
        </div>
      )}
      {enrolledCourses.length > 0 && (
        <div className={`${CARD} p-6 md:p-7`} data-testid="portal-notification-settings">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-lg font-semibold">Notifications</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Choose how you'd like to hear from your tutors.</p>
          <label data-testid="portal-reply-email-toggle" className="flex items-center justify-between gap-3 rounded-xl border p-3.5 cursor-pointer" style={{ borderColor: "var(--sj-border)", background: "#F2F6F5" }}>
            <div className="flex items-center gap-2 min-w-0">
              <MessagesSquare className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} />
              <span className="text-sm font-medium">Email me when a tutor replies to my assignment feedback</span>
            </div>
            <input data-testid="portal-reply-email-checkbox" type="checkbox" checked={replyEmailOptIn} disabled={savingReplyEmail} onChange={toggleReplyEmail} className="h-5 w-5 accent-[#0B5E4E] shrink-0" />
          </label>
        </div>
      )}
      {latestSermon && (() => {
        const isLink = !!latestSermon.is_link || latestSermon.content_type === "link";
        const li = isLink ? sermonLinkInfo(latestSermon.source_url || "") : null;
        return (
          <div className={`${CARD} p-6 md:p-7`} data-testid="dashboard-latest-sermon">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Mic className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
                <h3 className="font-head text-lg font-semibold">Latest sermon</h3>
              </div>
              <button data-testid="dashboard-all-sermons" onClick={() => setTab("sermons")} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}>All sermons <ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="h-14 w-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
                {isLink && li.platform === "youtube" ? <Youtube className="h-7 w-7" style={{ color: "#0B5E4E" }} /> : isLink && li.platform === "facebook" ? <Facebook className="h-7 w-7" style={{ color: "#0B5E4E" }} /> : <Mic className="h-7 w-7" style={{ color: "#0B5E4E" }} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-head text-lg font-semibold leading-tight">{latestSermon.title}</div>
                <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>{latestSermon.speaker || "Streams of Joy"}{latestSermon.sermon_date ? ` · ${latestSermon.sermon_date}` : ""}{latestSermon.series ? ` · ${latestSermon.series}` : ""}</div>
              </div>
              <button data-testid="dashboard-watch-sermon" onClick={() => setTab("sermons")} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold shrink-0 hover:brightness-105" style={{ background: "var(--sj-secondary)" }}>
                <Play className="h-4 w-4" /> Watch now
              </button>
            </div>
          </div>
        );
      })()}
      </>)}

      {/* Announcements from the church (read-only) */}
      {tab === "messages" && (
      <PortalMessages onChange={loadConversation} />
      )}

      {/* My Courses */}
      {tab === "courses" && (<>
      <div className={`${CARD} p-6 md:p-8`} data-testid="portal-courses">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-xl font-semibold">My Courses</h3>
          </div>
          <button data-testid="portal-browse-courses" onClick={() => navigate("/my-classes")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
            <BookOpen className="h-4 w-4" /> Browse all courses
          </button>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>The classes you're enrolled in and your progress in each.</p>
        {enrolledCourses.length === 0 ? (
          <div className="text-center py-10 flex flex-col items-center gap-3" style={{ color: "var(--sj-text-3)" }} data-testid="portal-courses-empty">
            <GraduationCap className="h-10 w-10" /> You're not enrolled in any course yet.
          </div>
        ) : (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="portal-enrolled-courses">
            {coursesPaged.pageItems.map((c) => (
              <button key={c.id} data-testid={`portal-course-${c.id}`} onClick={() => navigate(`/my-classes/${c.id}`)} className="rounded-2xl border p-5 text-left hover:shadow-md transition-all" style={{ borderColor: "var(--sj-border)" }}>
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#17A88A1A" }}><BookOpen className="h-6 w-6" style={{ color: "#17A88A" }} /></div>
                  <ChevronRight className="h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                </div>
                <h4 className="mt-3 font-head text-lg font-semibold leading-tight">{c.title}</h4>
                {c.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--sj-text-2)" }}>{c.description}</p>}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1" style={{ color: "var(--sj-text-3)" }}>
                    <span>{c.progress?.completed || 0}/{c.progress?.total || 0} lessons</span>
                    <span>{c.progress?.percent || 0}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--sj-border)" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.progress?.percent || 0}%`, background: "var(--sj-secondary)" }} />
                  </div>
                </div>
                {c.cert && (c.cert.issued ? (
                  <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold" data-testid={`course-cert-earned-${c.id}`} style={{ background: "#17A88A18", color: "#084438" }}>
                    <Award className="h-4 w-4" /> Certificate earned 🎓
                  </div>
                ) : c.cert.eligible ? (
                  <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold" data-testid={`course-cert-ready-${c.id}`} style={{ background: "#17A88A18", color: "#084438" }}>
                    <Sparkles className="h-4 w-4" /> Certificate ready — ask your tutor to issue it!
                  </div>
                ) : (c.cert.steps_remaining?.length && (c.cert.attendance_percent > 0 || c.cert.required_passed > 0)) ? (
                  <div className="mt-3 rounded-xl px-3 py-2" data-testid={`course-cert-nudge-${c.id}`} style={{ background: "#FBEAD7", color: "#8a5a1a" }}>
                    <div className="text-xs font-bold uppercase tracking-wide flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Almost certified</div>
                    <div className="text-sm mt-0.5">{c.cert.steps_remaining[0]}{c.cert.steps_remaining.length > 1 ? ` · ${c.cert.steps_remaining[1]}` : ""}</div>
                  </div>
                ) : null)}
              </button>
            ))}
          </div>
          <Paginator page={coursesPaged.page} pageCount={coursesPaged.pageCount} onPage={coursesPaged.setPage} testid="portal-courses-paginator" />
        </>)}
      </div>

      {/* My Certificates */}
      {certs.length > 0 && (
        <div className={`${CARD} p-6 md:p-8`} data-testid="portal-certificates">
          <div className="flex items-center gap-2 mb-1">
            <Award className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-xl font-semibold">My Certificates</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Courses you've completed — download your Certificate of Completion any time.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {certs.map((c) => (
              <div key={c.course_id} data-testid={`portal-cert-${c.course_id}`}
                className="relative overflow-hidden rounded-2xl p-5 flex flex-col gap-3"
                style={{ background: "linear-gradient(115deg, rgba(11,46,41,0.07), rgba(11,94,78,0.12))", border: "1px solid var(--sj-border)" }}>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--sj-primary)" }}>
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-head font-bold leading-tight truncate">{c.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>
                      Issued {c.issued_at ? new Date(c.issued_at).toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" }) : "recently"}
                      {c.issued_by ? ` · by ${c.issued_by}` : ""}
                    </div>
                  </div>
                </div>
                <button data-testid={`portal-cert-download-${c.course_id}`} onClick={() => downloadCert(c)} disabled={downloadingCert === c.course_id}
                  className="self-start inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                  <Download className="h-4 w-4" /> {downloadingCert === c.course_id ? "Preparing..." : "Download"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}

      {/* Latest Sermons */}
      {tab === "sermons" && (
      <div className={`${CARD} p-6 md:p-8`} data-testid="portal-sermons">
        <div className="flex items-center gap-2 mb-1">
          <Mic className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <h3 className="font-head text-xl font-semibold">Latest Sermons</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Catch up on recent messages — watch or listen right here.</p>
        {sermonSeries.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="portal-sermon-series-filter">
            <button data-testid="portal-series-all" onClick={() => setPortalSeries("")} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors" style={portalSeries === "" ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>All</button>
            {sermonSeries.map((sr) => (
              <button key={sr} data-testid={`portal-series-${sr}`} onClick={() => setPortalSeries(sr)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors" style={portalSeries === sr ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}>
                <Layers className="h-3 w-3" /> {sr}
              </button>
            ))}
          </div>
        )}
        {portalSeries && <p className="text-xs mb-3" style={{ color: "var(--sj-text-3)" }}>Following <b>{portalSeries}</b> in order — oldest to newest.</p>}
        {sermonsList.length === 0 ? (
          <div className="text-center py-10 flex flex-col items-center gap-3" style={{ color: "var(--sj-text-3)" }} data-testid="portal-sermons-empty">
            <Mic className="h-10 w-10" /> No sermons available yet.
          </div>
        ) : (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-sermons-grid">
            {sermonsPaged.pageItems.map((s) => {
              const isLink = !!s.is_link || s.content_type === "link";
              const li = isLink ? sermonLinkInfo(s.source_url || "") : null;
              const k = sermonKind(s.content_type);
              const Icon = isLink ? (li.platform === "youtube" ? Youtube : li.platform === "facebook" ? Facebook : Link2) : (k === "audio" ? Mic : k === "video" ? Video : FileText);
              const canEmbed = isLink && !!li.embed;
              return (
                <div key={s.id} data-testid={`portal-sermon-${s.id}`} className="rounded-2xl border p-5 flex flex-col" style={{ borderColor: "var(--sj-border)" }}>
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}><Icon className="h-6 w-6" style={{ color: "#0B5E4E" }} /></div>
                    {isLink && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full" style={{ background: "#0B5E4E1A", color: "#0B5E4E" }}>{li.label}</span>}
                  </div>
                  <h4 className="mt-3 font-head text-base font-semibold leading-tight">{s.title}</h4>
                  <div className="text-xs mt-1" style={{ color: "var(--sj-text-2)" }}>{s.speaker || "Streams of Joy"}{s.sermon_date ? ` · ${s.sermon_date}` : ""}</div>
                  {s.series && <button data-testid={`portal-sermon-series-${s.id}`} onClick={() => setPortalSeries(s.series)} className="mt-1.5 self-start inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full hover:brightness-95" style={{ background: "#17A88A1A", color: "#084438" }}><Layers className="h-3 w-3" /> {s.series}</button>}
                  {s.description && <p className="text-sm mt-2 line-clamp-2 flex-1" style={{ color: "var(--sj-text-2)" }}>{s.description}</p>}
                  {playingSermon === s.id && !isLink && k === "audio" && <audio src={mediaUrl(s.id)} controls autoPlay className="mt-3 w-full" />}
                  {playingSermon === s.id && !isLink && k === "video" && <video src={mediaUrl(s.id)} controls autoPlay className="mt-3 w-full rounded-lg" />}
                  {playingSermon === s.id && canEmbed && (
                    <div className="mt-3 w-full rounded-lg overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                      <iframe title={s.title} src={li.embed} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                    </div>
                  )}
                  <div className="mt-4 flex gap-2 flex-wrap">
                    {(canEmbed || (!isLink && k !== "doc")) && (
                      <button data-testid={`portal-sermon-play-${s.id}`} onClick={() => setPlayingSermon(playingSermon === s.id ? null : s.id)} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}>
                        <Play className="h-3.5 w-3.5" /> {playingSermon === s.id ? "Stop" : "Play"}
                      </button>
                    )}
                    {isLink ? (
                      <a data-testid={`portal-sermon-watch-${s.id}`} href={s.source_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><ExternalLink className="h-3.5 w-3.5" /> Watch on {li.label}</a>
                    ) : (
                      <a data-testid={`portal-sermon-download-${s.id}`} href={mediaUrl(s.id)} target="_blank" rel="noreferrer" download className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><Download className="h-3.5 w-3.5" /> {k === "doc" ? "Open" : "Download"}</a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <Paginator page={sermonsPaged.page} pageCount={sermonsPaged.pageCount} onPage={sermonsPaged.setPage} testid="portal-sermons-paginator" />
        </>)}
      </div>
      )}

      {tab === "documents" && (<>
      {/* My Documents */}
      {(myDocs.files.length > 0 || myDocs.collab.length > 0) && (
        <div className={`${CARD} p-6 md:p-8`} data-testid="portal-documents">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-xl font-semibold">My Documents</h3>
          </div>
          <p className="text-sm mb-4 inline-flex items-center gap-1.5" style={{ color: "var(--sj-text-2)" }}>
            <Lock className="h-3.5 w-3.5" /> Files your team leaders have shared with you.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {myDocs.files.map((d) => (
              <div key={d.id} data-testid={`portal-doc-${d.id}`} className="rounded-xl border p-4 flex items-center gap-3 transition-all" style={{ borderColor: highlightDoc === d.id ? "var(--sj-primary)" : "var(--sj-border)", boxShadow: highlightDoc === d.id ? "0 0 0 3px #0B5E4E33" : "none" }}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--sj-peach)" }}><FileText className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-sm">{d.title}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{d.ext?.toUpperCase()}{d.team_name ? ` · ${d.team_name}` : ""}</div>
                </div>
                <button data-testid={`portal-download-${d.id}`} onClick={() => downloadDoc(d)} className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 text-white hover:brightness-105 shrink-0" style={{ background: "var(--sj-secondary)" }}><Download className="h-3.5 w-3.5" />Open</button>
              </div>
            ))}
            {myDocs.collab.map((d) => (
              <a key={d.id} href={d.url} target="_blank" rel="noreferrer" data-testid={`portal-collab-${d.id}`} className="rounded-xl border p-4 flex items-center gap-3 hover:bg-[var(--sj-bg)] transition-colors" style={{ borderColor: "var(--sj-border)" }}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}><ExternalLink className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-sm">{d.title}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>Google document{d.team_name ? ` · ${d.team_name}` : ""}</div>
                </div>
                <span className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border shrink-0" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>Open</span>
              </a>
            ))}
          </div>
        </div>
      )}


      {myDocs.files.length === 0 && myDocs.collab.length === 0 && (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }} data-testid="portal-documents-empty">
          <FileText className="h-12 w-12" /> No documents yet. Files your team leaders share with you will appear here.
        </div>
      )}
      </>)}


      {/* Prayer requests */}
      {tab === "prayer" && (<>
      <div className={`${CARD} p-6 md:p-8`} data-testid="portal-prayer">
        <div className="flex items-center gap-2 mb-1">
          <HandHeart className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <h3 className="font-head text-xl font-semibold">Prayer Requests</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Share a request and our pastoral team will pray with you.</p>
        <form onSubmit={submitPrayer} className="space-y-3">
          <textarea data-testid="prayer-input" rows={3} value={prayerText} onChange={(e) => setPrayerText(e.target.value)}
            placeholder="What can we pray about for you?"
            className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-3 text-sm" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--sj-text-2)" }}>
              <input data-testid="prayer-private" type="checkbox" checked={prayerPrivate} onChange={(e) => setPrayerPrivate(e.target.checked)} className="h-4 w-4 accent-[#0B5E4E]" />
              Mark as confidential — only pastors can read it
            </label>
            <button type="submit" data-testid="prayer-submit" disabled={submittingPrayer || !prayerText.trim()}
              className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
              {submittingPrayer ? "Sending..." : "Send Prayer Request"}
            </button>
          </div>
        </form>
        {prayers.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="my-prayers">
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Your requests &amp; testimonies</div>
            {prayers.map((p) => {
              const label = { new: "Received", praying: "Being prayed for", answered: "Answered 🙌" }[p.status] || "Received";
              const tint = p.status === "answered" ? "#17A88A" : p.status === "praying" ? "#084438" : "#56626A";
              return (
                <div key={p.id} data-testid={`my-prayer-${p.id}`} className="rounded-xl border p-3" style={{ borderColor: "var(--sj-border)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text)" }}>{p.request}</p>
                      <div className="text-xs mt-1" style={{ color: "var(--sj-text-3)" }}>{new Date(p.created_at).toLocaleDateString('en-GB', { month: "short", day: "numeric" })}{p.is_private ? " · Confidential" : ""}</div>
                    </div>
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0" style={{ background: `${tint}1A`, color: tint }}>{label}</span>
                  </div>
                  {p.testimony && (
                    <div className="mt-2 rounded-lg px-3 py-2" data-testid={`prayer-testimony-${p.id}`} style={{ background: "#17A88A14" }}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide mb-0.5 flex items-center gap-1.5" style={{ color: "#17A88A" }}><Sparkles className="h-3 w-3" /> My testimony {p.testimony_shared && (p.testimony_approved
                        ? <span className="normal-case font-medium px-1.5 py-0.5 rounded-full" style={{ background: "#0B5E4E", color: "#fff" }} data-testid={`testimony-shared-tag-${p.id}`}>On the wall</span>
                        : <span className="normal-case font-medium px-1.5 py-0.5 rounded-full" style={{ background: "#FBEAD7", color: "#8a5a1a" }} data-testid={`testimony-pending-tag-${p.id}`}>Pending review</span>)}</div>
                      <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--sj-text)" }}>{p.testimony}</p>
                    </div>
                  )}
                  <div className="mt-2 flex justify-end">
                    {p.status === "answered" ? (
                      <button data-testid={`prayer-edit-testimony-${p.id}`} onClick={() => openAnswer(p)} className="text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>{p.testimony ? "Edit testimony" : "Add a testimony"}</button>
                    ) : (
                      <button data-testid={`prayer-mark-answered-${p.id}`} onClick={() => openAnswer(p)} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}><Check className="h-3.5 w-3.5" /> Mark as answered</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {testimonies.length > 0 && (
        <div className={`${CARD} p-6 md:p-8`} data-testid="portal-testimony-wall">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-xl font-semibold">Testimony Wall</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>Answered prayers shared by our church family — be encouraged! 🙌</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {testimonies.map((t) => (
              <div key={t.id} data-testid={`testimony-${t.id}`} className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: "linear-gradient(135deg, rgba(23,168,138,0.10), rgba(11,94,78,0.06))", border: "1px solid var(--sj-border)" }}>
                <HandHeart className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
                <p className="text-sm whitespace-pre-wrap flex-1" style={{ color: "var(--sj-text)" }}>{t.testimony}</p>
                <div className="text-xs font-semibold" style={{ color: "var(--sj-primary)" }}>— {t.name}{t.mine ? " (you)" : ""}{t.date ? ` · ${new Date(t.date).toLocaleDateString('en-GB', { month: "short", year: "numeric" })}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}

      {tab === "cell" && <MyCellGroupPanel />}

      {tab === "profile" && (
      <div className={`${CARD} p-6 md:p-8`} data-testid="portal-profile">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-head text-xl font-semibold">My Profile</h3>
          {member && (
            <button data-testid="portal-edit-profile-button" onClick={openEditProfile}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-colors hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
              <Pencil className="h-4 w-4" /> Edit profile
            </button>
          )}
        </div>
        {member ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Row icon={User} label="Full name" value={`${member.first_name} ${member.last_name}`} />
            <Row icon={Mail} label="Email" value={member.email || user.email} />
            <Row icon={Phone} label="Phone" value={member.phone || "—"} />
            <Row icon={MapPin} label="Address" value={member.address || "—"} />
            <Row icon={MapPin} label="Postcode" value={member.postcode || "—"} />
            <Row icon={CalendarCheck} label="Date of birth" value={ukDate(member.date_of_birth)} />
            <Row icon={CalendarCheck} label="Member since" value={ukDate(member.join_date)} />
            <Row icon={Heart} label="Membership type" value={member.membership_type || "Member"} />
          </div>
        ) : (
          <p style={{ color: "var(--sj-text-2)" }}>Your profile is being set up. Please contact a church leader.</p>
        )}
      </div>
      )}

      <Dialog open={!!answerFor} onOpenChange={(o) => { if (!o) { setAnswerFor(null); setTestimonyText(""); setShareTestimony(false); } }}>
        <DialogContent data-testid="prayer-answer-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Answered prayer 🙌</DialogTitle></DialogHeader>
          <form onSubmit={saveAnswer} className="space-y-3 mt-2">
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Marking <b>your</b> prayer as answered. Add a short testimony to remember how God came through (optional, private to you).</p>
            <div className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--sj-peach)", color: "var(--sj-text-2)" }}>{answerFor?.request}</div>
            <textarea data-testid="prayer-testimony-input" rows={4} value={testimonyText} onChange={(e) => setTestimonyText(e.target.value)}
              placeholder="Share your testimony…"
              className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-3 text-sm" />
            <label className="flex items-start gap-2.5 cursor-pointer text-sm rounded-xl px-3 py-2.5" style={{ background: "#17A88A0F", color: "var(--sj-text-2)" }}>
              <input type="checkbox" data-testid="prayer-share-toggle" checked={shareTestimony} onChange={(e) => setShareTestimony(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#0B5E4E]" />
              <span>Share this testimony on the church <b>Testimony Wall</b> to encourage others (shows your first name). A pastor will review it before it appears.</span>
            </label>
            <DialogFooter>
              <button type="button" onClick={() => { setAnswerFor(null); setTestimonyText(""); setShareTestimony(false); }} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="prayer-answer-save" disabled={savingAnswer} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingAnswer ? "Saving…" : "Save as answered"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="portal-edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">Edit my profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <ProfilePhotoEditor member={member} onChange={() => api.get("/portal/me").then((r) => setData(r.data))} />
            <PField label="First name">
              <input data-testid="profile-first-name" required value={pform.first_name} onChange={(e) => setPform({ ...pform, first_name: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Last name">
              <input data-testid="profile-last-name" required value={pform.last_name} onChange={(e) => setPform({ ...pform, last_name: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Email">
              <input data-testid="profile-email" type="email" value={pform.email} onChange={(e) => setPform({ ...pform, email: e.target.value })} className={PINPUT} />
              {user.role !== "member" && <p className="text-[11px] mt-1" style={{ color: "var(--sj-text-3)" }}>Contact email for the church directory — this does not change your login email.</p>}
            </PField>
            <PField label="Phone">
              <input data-testid="profile-phone" value={pform.phone} onChange={(e) => setPform({ ...pform, phone: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Date joined">
              <input data-testid="profile-join-date" type="date" value={pform.join_date} onChange={(e) => setPform({ ...pform, join_date: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Date of birth">
              <input data-testid="profile-date-of-birth" type="date" value={pform.date_of_birth} onChange={(e) => setPform({ ...pform, date_of_birth: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Address">
              <input data-testid="profile-address" value={pform.address} onChange={(e) => setPform({ ...pform, address: e.target.value })} className={PINPUT} />
            </PField>
            <PField label="Postcode">
              <input data-testid="profile-postcode" value={pform.postcode} onChange={(e) => setPform({ ...pform, postcode: e.target.value })} className={PINPUT} />
            </PField>
            <ProfileExtraFields form={pform} setForm={setPform} inputCls={PINPUT} />
            <PField label="Membership type">
              <input data-testid="profile-membership-type" value={member?.membership_type || "Member"} disabled readOnly
                className={`${PINPUT} opacity-60 cursor-not-allowed`} title="Only a church leader can change your membership type" />
            </PField>
            <div className="sm:col-span-2 -mt-1">
              <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Membership type, status and pastoral notes can only be changed by church staff.</p>
            </div>
            <DialogFooter className="sm:col-span-2 mt-2">
              <button type="button" onClick={() => setEditOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-medium border transition-colors hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="profile-save-button" disabled={savingProfile}
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50 transition-opacity" style={{ background: "var(--sj-primary)" }}>
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PINPUT = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

function PField({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--sj-peach)" }}>
        <Icon className="h-4.5 w-4.5" style={{ color: "var(--sj-primary)" }} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>{label}</div>
        <div className="font-medium" style={{ color: "var(--sj-text)" }}>{value}</div>
      </div>
    </div>
  );
}
